<template>
  <div class="login-page" :class="{ 'dark-mode': isDarkMode }">
    <main class="login-main">
      <div class="login-container">
        <div class="login-page-branding">
          <img src="../../assets/images/ppt/pavicon_login_ppt.png" alt="Pavicon Logo" class="login-logo login-logo-outside" />
        </div>
        <div class="login-form-wrapper">
          <form class="login-form" @submit.prevent="submit">
            <div class="form-group">
              <input
                type="password"
                v-model="password"
                placeholder="New password"
                required
                class="form-input"
              />
            </div>

            <div class="form-group">
              <input
                type="password"
                v-model="confirmPassword"
                placeholder="Confirm new password"
                required
                class="form-input"
              />
            </div>

            <div class="form-group">
              <button type="submit" :disabled="loading" class="continue-btn">
                <span v-if="loading" class="btn-loading">
                  <span class="spinner" aria-hidden="true"></span>
                  <span class="loading-text">Please wait...</span>
                </span>
                <span v-else>SET NEW PASSWORD</span>
              </button>
            </div>

            <div class="back-to-login-wrapper">
              <router-link to="/" class="back-to-login-link">Back to Login</router-link>
            </div>
          </form>
        </div>
      </div>
    </main>
  </div>
</template>

<script>
import Swal from 'sweetalert2'
import { useDarkMode } from '../../composables/useDarkMode'

export default {
  name: 'ResetPassword',
  setup() {
    const { isDarkMode } = useDarkMode()
    return { isDarkMode }
  },
  data: () => ({
    password: '',
    confirmPassword: '',
    loading: false,
    token: null,
    email: null,
    businessLogo: '',
    systemName: ''
  }),
  mounted() {
    const params = new URLSearchParams(window.location.search)
    this.token = params.get('token')
    this.email = params.get('email')
    const profileData = localStorage.getItem('profile')
    const profile = profileData && profileData !== 'undefined' ? JSON.parse(profileData) : null
    this.businessLogo = profile?.businessLogo || ''
    this.systemName = profile?.systemName || ''
  },
  methods: {
    async submit() {
      if (!this.token || !this.email) {
        Swal.fire({ title: 'Invalid link', text: 'Missing token or email', icon: 'error' })
        return
      }
      if (this.password.length < 6) {
        Swal.fire({ title: 'Invalid password', text: 'Password must be at least 6 characters', icon: 'error' })
        return
      }
      if (this.password !== this.confirmPassword) {
        Swal.fire({ title: 'Mismatch', text: 'Passwords do not match', icon: 'error' })
        return
      }
      this.loading = true
      try {
        const res = await fetch(process.env.VUE_APP_URL + '/auth/reset/confirm', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: this.email, token: this.token, password: this.password })
        })
        const data = await res.json()
        if (data.status === true) {
          Swal.fire({ title: 'Success', text: data.message, icon: 'success' })
          this.$router.push('/')
        } else {
          Swal.fire({ title: 'Failed', text: data.message || 'Reset failed', icon: 'error' })
        }
      } catch (err) {
        Swal.fire({ title: 'Error', text: err.message || 'Request failed', icon: 'error' })
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
* {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

.login-page {
  --bg-primary: #f8f9fa;
  --bg-secondary: #fff;
  --text-primary: #333;
  --text-secondary: #6c757d;
  --border-color: #e9ecef;
  --border-input: #ced4da;
  --shadow: rgba(0, 0, 0, 0.1);
  --accent-color: #007bff;
  --accent-hover: #0056b3;
  --button-primary: #ff6b35;
  --button-hover: #e55a2e;

  min-height: 100vh;
  background-color: var(--bg-primary);
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', sans-serif;
  display: flex;
  flex-direction: column;
  color: var(--text-primary);
  transition: background-color 0.3s ease, color 0.3s ease;
}

.login-page.dark-mode {
  --bg-primary: #1a1a1a;
  --bg-secondary: #2d2d2d;
  --text-primary: #e0e0e0;
  --text-secondary: #a0a0a0;
  --border-color: #404040;
  --border-input: #555;
  --shadow: rgba(0, 0, 0, 0.4);
  --accent-color: #4dabf7;
  --accent-hover: #339af0;
  --button-primary: #ff7849;
  --button-hover: #ff6b35;
}

.login-main {
  flex: 1;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 4.5rem 1rem 2rem;
}

.login-container {
  width: 100%;
  max-width: clamp(280px, 22vw, 360px);
}

.login-page-branding {
  display: flex;
  justify-content: center;
  margin-bottom: 0.9rem;
}

.login-form-wrapper {
  background: var(--bg-secondary);
  border-radius: 8px;
  padding: 1.9rem 1.75rem;
  border: 1px solid var(--border-color);
  transition: background-color 0.3s ease, border-color 0.3s ease, box-shadow 0.3s ease;
}

.login-form-header {
  text-align: center;
  margin-bottom: 1.35rem;
}

.system-branding {
  margin-bottom: 1.1rem;
}

.login-logo {
  height: 34px;
  width: auto;
  margin-bottom: 0.6rem;
}

.login-logo-outside {
  margin-bottom: 0;
}

.system-description {
  font-size: 13px;
  color: var(--text-secondary);
  margin: 0 0 1rem 0;
  font-weight: 400;
}

.login-title {
  font-size: 24px;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 0.5rem;
}

.signup-text {
  color: var(--text-secondary);
  font-size: 13px;
}

.login-form {
  width: 100%;
}

.form-group {
  margin-bottom: 0.85rem;
}

.form-input {
  width: 100%;
  padding: 11px 13px;
  border: 1px solid var(--border-input);
  border-radius: 4px;
  font-size: 13px;
  background-color: var(--bg-secondary);
  color: var(--text-primary);
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out, background-color 0.3s ease;
}

.form-input:focus {
  border-color: var(--accent-color);
  outline: 0;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

.form-input::placeholder {
  color: var(--text-secondary);
}

.continue-btn {
  width: 100%;
  background-color: #4f506e;
  color: white;
  border: none;
  padding: 11px 24px;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.15s ease-in-out;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.continue-btn:hover {
  background-color: #4338ca;
}

.continue-btn:active {
  background-color: #3730a3;
}

.continue-btn[disabled] {
  opacity: 0.7;
  cursor: not-allowed;
}

.btn-loading {
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: center;
}

.spinner {
  width: 13px;
  height: 13px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: #fff;
  animation: spin 0.8s linear infinite;
  display: inline-block;
}

.loading-text {
  font-size: 13px;
  color: #fff;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.back-to-login-wrapper {
  text-align: center;
  margin-top: 1rem;
}

.back-to-login-link {
  color: #4f506e;
  font-size: 13px;
  text-decoration: none;
}

.back-to-login-link:hover {
  color: #4338ca;
  text-decoration: underline;
}

@media (max-width: 1100px) {
  .login-form-wrapper {
    padding: 1.6rem 1.35rem;
  }
}

@media (max-width: 768px) {
  .login-main {
    padding-top: 3rem;
  }

  .login-form-wrapper {
    padding: 1.55rem 1.25rem;
    margin: 0 1rem;
  }
}

@media (max-width: 480px) {
  .login-main {
    padding-top: 2rem;
  }

  .login-form-wrapper {
    padding: 1.3rem 0.95rem;
  }

  .login-title {
    font-size: 20px;
  }

  .continue-btn {
    font-size: 13px;
    padding: 10px 20px;
  }
}
</style>
